import React from 'react';
import { motion } from 'framer-motion';
import { GoDotFill } from "react-icons/go";
import { AiOutlineTwitter } from "react-icons/ai";
import { BsLinkedin, BsGithub } from "react-icons/bs";
import { FaMediumM } from "react-icons/fa";

// Use the newly optimized profile.jpg
import newProfile from "../../assets/profile.jpg";
// In case they haven't uploaded it yet, importing utibe.jpg as fallback if it fails, but webpack might throw if file not found. 
// A better approach is pointing to public or just keeping the import and expecting the user to add it.
// I'll keep the import simple. If it fails compilation before they add it, they know what to do.

const Home = () => {
  return (
    <section id='home' className='relative min-h-screen flex items-center justify-center pt-24 pb-16 overflow-hidden'>

      {/* Background blobs for modern slick look */}
      <div className="absolute top-20 -left-20 w-72 h-72 bg-primary/20 rounded-full mix-blend-screen filter blur-[80px] animate-blob z-0"></div>
      <div className="absolute top-40 -right-20 w-72 h-72 bg-secondary/20 rounded-full mix-blend-screen filter blur-[80px] animate-blob animation-delay-2000 z-0"></div>
      <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-accent/20 rounded-full mix-blend-screen filter blur-[80px] animate-blob animation-delay-4000 z-0"></div>

      <div className='max-w-7xl mx-auto px-6 lg:px-8 w-full z-10'>
        <div className='flex flex-col-reverse lg:flex-row items-center justify-between gap-16'>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className='lg:w-1/2 flex flex-col items-start'
          >
            <div className="inline-flex items-center space-x-2 bg-slate-800/50 rounded-full px-4 py-2 mb-6 border border-slate-700/50 backdrop-blur-sm">
              <GoDotFill className="text-secondary animate-pulse" />
              <span className="text-sm font-medium text-slate-300">Currently Building @ Miva</span>
            </div>

            <h1 className='text-5xl md:text-7xl font-bold text-white leading-tight mb-4'>
              Hi! I'm <br />
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary'>
                Utibe Etim.
              </span>
            </h1>

            <h2 className='text-2xl md:text-3xl font-medium text-slate-400 mb-6'>
              Software Engineer & Developer Advocate
            </h2>

            <p className='text-lg text-slate-400 mb-10 leading-relaxed max-w-lg'>
              I specialize in writing clean code and building scalable, pixel-perfect applications. While my core expertise lies in mobile engineering using <span className="text-white font-medium">Java, Kotlin, Jetpack Compose, React Native, and Flutter</span>, I am equally passionate about crafting dynamic web experiences with <span className="text-white font-medium">React, Next.js, and TailwindCSS</span>.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#experience"
                className="px-8 py-4 bg-primary text-white font-medium rounded-full hover:bg-primary/90 flex items-center transition-all hover:-translate-y-1 shadow-[0_0_20px_rgba(79,70,229,0.4)]"
              >
                View Experience
              </a>
              <a
                href="#contact"
                className="px-8 py-4 bg-slate-800 text-white font-medium rounded-full hover:bg-slate-700 flex items-center transition-all hover:-translate-y-1 border border-slate-700"
              >
                Contact Me
              </a>
            </div>

            {/* Quick Social Links */}
            <div className="flex items-center space-x-3 mt-8">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Connect:
              </span>
              <div className="flex space-x-2 items-center">
                <a
                  href="https://github.com/Uticodes"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="GitHub Profile"
                  title="GitHub"
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800/80 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <BsGithub size={20} />
                </a>
                <a
                  href="https://www.linkedin.com/in/utibe-etim-0a901a107/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn Profile"
                  title="LinkedIn"
                  className="p-2 text-slate-400 hover:text-secondary hover:bg-slate-800/80 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <BsLinkedin size={20} />
                </a>
                <a
                  href="https://twitter.com/Uticodes"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Twitter Profile"
                  title="Twitter"
                  className="p-2 text-slate-400 hover:text-primary hover:bg-slate-800/80 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <AiOutlineTwitter size={22} />
                </a>
                <a
                  href="https://medium.com/@utibeetim"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Medium Profile"
                  title="Medium"
                  className="p-2 text-slate-400 hover:text-accent hover:bg-slate-800/80 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <FaMediumM size={19} />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Image Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className='lg:w-1/2 flex justify-center relative'
          >
            {/* Aesthetic Image Wrapper */}
            <div className="relative w-[300px] h-[340px] sm:w-[380px] sm:h-[420px] md:w-[420px] md:h-[480px]">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary to-secondary rounded-[2rem] transform rotate-6 opacity-30 blur-lg"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-primary to-secondary rounded-[2rem] transform rotate-3"></div>
              <img
                // We rely on the user adding profile.png to assets. If it crashes, webpack will tell them.
                src={newProfile}
                alt="Utibe Etim - Profile"
                fetchpriority="high"
                loading="eager"
                className='absolute inset-0 w-full h-full object-cover rounded-[2rem] border-4 border-slate-900 shadow-2xl z-10'
                onError={(e) => {
                  e.target.onerror = null;
                  // Fallback visual if image not found to avoid completely broken layout
                  e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect fill="%231e293b" width="400" height="400"/><text fill="%2394a3b8" x="50%" y="50%" font-family="sans-serif" font-size="20" text-anchor="middle">Add profile.png to src/assets</text></svg>';
                }}
              />

              {/* Floating elements for aesthetic */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                className="absolute -top-6 -right-6 bg-slate-800/80 backdrop-blur-md p-4 rounded-2xl border border-slate-700 z-20 shadow-xl"
              >
                <span className="text-2xl">📱</span>
              </motion.div>

              <motion.div
                animate={{ y: [0, 15, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 bg-slate-800/80 backdrop-blur-md p-4 rounded-2xl border border-slate-700 z-20 shadow-xl"
              >
                <span className="text-2xl">💻</span>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  )
}

export default Home;
